// @ts-nocheck
import * as React from 'react';

import { cn } from '../../lib/utils';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  showCharacterCount?: boolean;
  maxLength?: number;
  autoResize?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      error,
      showCharacterCount = false,
      maxLength,
      autoResize = false,
      value: controlledValue,
      defaultValue,
      onChange: controlledOnChange,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(controlledValue ?? defaultValue ?? '');
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    // Use controlled value if provided, otherwise use internal state
    const isControlled = controlledValue !== undefined;
    const value = isControlled ? controlledValue : internalValue;

    // Auto-resize functionality
    React.useEffect(() => {
      if (autoResize && textareaRef.current) {
        const textarea = textareaRef.current;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }, [value, autoResize]);

    // Handle value changes for character count and controlled/uncontrolled modes
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!isControlled) {
        setInternalValue(e.target.value);
      }
      if (controlledOnChange) {
        controlledOnChange(e);
      }
    };

    const characterCount = typeof value === 'string' ? value.length : 0;

    return (
      <div className="space-y-1">
        {label && (
          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            {label}
            {maxLength && showCharacterCount && (
              <span className="ml-2 text-muted-foreground">
                ({characterCount}/{maxLength})
              </span>
            )}
          </label>
        )}
        <textarea
          className={cn(
            'flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-destructive focus-visible:ring-destructive',
            autoResize && 'resize-none overflow-hidden',
            className
          )}
          ref={(node) => {
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
            textareaRef.current = node;
          }}
          value={value}
          onChange={handleChange}
          maxLength={maxLength}
          {...props}
        />
        <div className="flex justify-between items-center">
          {error && <p className="text-sm text-destructive">{error}</p>}
          {showCharacterCount && maxLength && !error && (
            <p
              className={cn(
                'text-xs text-muted-foreground ml-auto',
                characterCount > maxLength * 0.9 && 'text-amber-600',
                characterCount >= maxLength && 'text-red-600'
              )}
            >
              {characterCount}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';

export { Textarea };
